import arrayMutators from 'final-form-arrays';
import { func } from 'prop-types';
import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { CustomExtendedDataField, FieldTextInput, Form, PrimaryButton } from '../../components';
import {
  FormattedMessage,
  intlShape
} from '../../util/reactIntl';
import classNames from 'classnames';
import PhoneInput from 'react-phone-input-2';
import * as validators from '../../util/validators';
import css from "./MerchantDetails.module.css";
import { getPropsForCustomUserFieldInputs } from '../../util/userHelpers';

// Normalize KSA MSISDN to 966XXXXXXXXX (no leading +). Accepted: 5, 05, 9665, +9665, 009665.
// 05XXXXXXXX → 96605XXXXXXXX (13 digits); 5XXXXXXXX → 9665XXXXXXXX (12 digits).
const normalizeMsisdn = raw => {
  if (!raw) return null;
  let s = String(raw).trim().replace(/\s+/g, '');
  if (s.startsWith('+')) s = s.slice(1);
  if (s.startsWith('00966')) s = s.replace(/^00966/, '966');
  if (s.startsWith('966')) return s;
  if (s.startsWith('05')) return '966' + s.slice(1);
  if (s.startsWith('5')) return '966' + s;
  return s;
};

// Validate KSA mobile. Accept 5XXXXXXXX, 05XXXXXXXX, 9665..., +9665..., 009665...
// 9665XXXXXXXX (12 digits) or 96605XXXXXXXX (13 digits).
const isValidKsaMsisdnInput = rawValue => {
  if (!rawValue) return false;
  const norm = normalizeMsisdn(String(rawValue));
  if (!norm) return false;
  return /^9665\d{8}$/.test(norm) || /^96605\d{8}$/.test(norm);
};

const SignupFlowForm = props => {
  if (typeof window === 'undefined') return null;
  require('../../styles/react-phone-input-2.css');

  const [phoneTouched, setPhoneTouched] = useState(false);

  return (
    <FinalForm
      {...props}
      mutators={{
        ...arrayMutators,
      }}
      render={formRenderProps => {
        const {
          disabled,
          handleSubmit,
          intl,
          invalid,
          hideFieldLabels,
          pristine,
          updated,
          formId,
          updateError,
          updateInProgress,
          updateSuccess,
          values,
          form,
          successToaster,
          errorToaster,
          currentUser,
          config,
        } = formRenderProps;

        const { protectedData, publicData } = currentUser?.attributes?.profile || {};
        const { userType } = publicData || {};
        const isMerchant = userType === 'provider';

        const { userFields, userTypes = [] } = config.user;

        // Custom user fields - filter to only show gender and dateOfBirth
        const allUserFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);
        const userFieldProps = allUserFieldProps.filter(field =>
          field.key === 'prot_gender' || field.key === 'prot_dateOfBirth'
        );

        // Determine if user type and custom fields should be shown
        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const errorMessage = updateError ? (
          <p className={css.error}>
            <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
          </p>
        ) : null;

        const submitReady = updated && pristine;
        const submitInProgress = updateInProgress;

        const { mobile_phone_number } = values || {};
        const hasPhone = !!mobile_phone_number?.phoneNumber;
        const rawForValidation =
          mobile_phone_number?.value ||
          (mobile_phone_number?.callingCode && mobile_phone_number?.phoneNumber
            ? `${mobile_phone_number.callingCode}${mobile_phone_number.phoneNumber}`
            : null);
        const isValidPhoneNumber = hasPhone
          ? isValidKsaMsisdnInput(rawForValidation)
          : false;

        const submitDisabled =
          invalid ||
          disabled ||
          submitInProgress ||
          !hasPhone ||
          !isValidPhoneNumber;

        const emailRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.emailRequired' })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({ id: 'SignupForm.emailInvalid' })
        );

        const handleChangePhoneInput = (value, country) => {
          const { dialCode = '', countryCode = '' } = country;
          const normalizedValue = normalizeMsisdn(value) || value;
          const normalizedWithoutDialCode =
            dialCode && String(normalizedValue).startsWith(String(dialCode))
              ? String(normalizedValue).slice(String(dialCode).length)
              : String(normalizedValue);
          form.change('phoneNumber', '+' + normalizedValue);
          const newState = {
            countryCode: countryCode?.toUpperCase(),
            callingCode: dialCode,
            phoneNumber: normalizedWithoutDialCode,
            value: normalizedValue,
          };
          form.change('mobile_phone_number', newState);
        };


        return (
          <Form onSubmit={handleSubmit}>
            <div className={css.defaultUserFields}>
              {!isMerchant ? (
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  className={css.inputBox}
                  autoComplete="off"
                  // label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder1' })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  disabled={true}
                />
              ) : null}

              {isMerchant ?
                <FieldTextInput
                  id={`${formId}business_name`}
                  name="business_name"
                  className={css.inputBox}
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'EditListingBusinessForm.businessNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'EditListingBusinessForm.businessNameRequired' })
                  )}
                // label={intl.formatMessage({
                //   id: 'EditListingBusinessForm.businessNamePlaceholder',
                // })}
                />
                : null}


              <PhoneInput
                className={css.phoneInput}
                name="phoneNumber"
                id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                country="sa"
                onlyCountries={['sa']}
                disableDropdown={true}
                value={values?.phoneNumber}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.phoneNumberLabel',
                })}
                specialLabel={intl.formatMessage({
                  id: 'SignupForm.phoneNumberLabel',
                })}
                onChange={handleChangePhoneInput}
                onBlur={() => setPhoneTouched(true)}
              />
              {phoneTouched && !hasPhone ? (
                <div className={css.errorText}>
                  <FormattedMessage id="SignupForm.phoneNumberRequired" />
                </div>
              ) : null}
              {phoneTouched && hasPhone && !isValidPhoneNumber ? (
                <div className={css.errorText}>
                  <FormattedMessage id="EditListingDetailsForm.phoneNumberInvalid" />
                </div>
              ) : null}


              {/* <div className={classNames(css.name)}> */}
              <FieldTextInput
                className={css.inputBox}
                type="text"
                id={formId ? `${formId}.firstName` : 'firstName'}
                name="firstName"
                autoComplete="off"
                // label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder1' })}
                validate={validators.required(
                  intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
                )}
              />
              <FieldTextInput
                className={css.inputBox}
                type="text"
                id={formId ? `${formId}.lastName` : 'lastName'}
                name="lastName"
                autoComplete="off"
                // label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder1' })}
                validate={validators.required(
                  intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
                )}
              />
              {/* </div> */}

              {isMerchant ?
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  className={css.inputBox}
                  autoComplete="off"
                  // label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder1' })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  disabled={true}
                />
                : null}

              {/* Date of Birth & Gender Fields */}
              {showCustomUserFields ? (
                <div className={classNames(css.inputBox, css.calenderInput)}>
                  {userFieldProps.map(fieldProps => (
                    <CustomExtendedDataField key={fieldProps.key} {...fieldProps} formId={formId} hideLabel={hideFieldLabels} />
                  ))}
                </div>
              ) : null}
            </div>

            {errorMessage ? errorMessage : null}

            <PrimaryButton
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady}
              className={css.button}
            >
              <FormattedMessage id="SignupFlowForm.submitButtonLabel" />
            </PrimaryButton>
          </Form>
        );
      }}
    />
  )
}

SignupFlowForm.defaultProps = {
  selectedPlace: null,
  updateError: null,
};

SignupFlowForm.propTypes = {
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  //   updateInProgress: bool.isRequired,
};

export default SignupFlowForm;